import { gql, useQuery } from '@apollo/client'
import { useState } from 'react'

import {
  ArtistMembershipFavoriteMembersFragmentDoc,
  ArtistMembershipFavoriteMembersSelectQuery,
  ArtistMembershipFavoriteMembersSelectQueryVariables,
  ArtistWithPatronFragmentDoc,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useRouter } from '@/core'
import { makeViewModel } from '@/core/architecture'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { router, generatePath, routerPages } =
    useRouter<{ artistId: string; productId: string }>()

  const artistId = router.query.artistId ?? config.artistId
  const localStorageKey = 'artistMembershipFavoriteMembersSelect'

  const { data, loading } = useQuery<
    ArtistMembershipFavoriteMembersSelectQuery,
    ArtistMembershipFavoriteMembersSelectQueryVariables
  >(QUERY, { variables: { artistId } })

  const [selectedFavoriteMember, setSelectedFavoriteMember] = useState<
    string | undefined
  >(() => {
    try {
      const storedData = localStorage.getItem(localStorageKey)
      if (storedData) {
        const parsedData = JSON.parse(storedData) as {
          id: string
          name: string
          avatar: string
        }
        return parsedData.id
      }
      return undefined
    } catch {
      return undefined
    }
  })

  return {
    state: {
      artist: data?.artist,
      artistId,
      selectedFavoriteMember,
      favoriteMembers: data?.artist?.favoriteMembers,
      loading,
      joined: !!data?.artist?.patron,
    },
    actions: {
      async onSubmit() {
        const selectedFavoriteMemberInfo = data?.artist?.favoriteMembers?.find(
          member => member?.id === selectedFavoriteMember
        )
        localStorage.setItem(
          localStorageKey,
          JSON.stringify({
            id: selectedFavoriteMemberInfo?.id,
            name: selectedFavoriteMemberInfo?.name,
            avatar: selectedFavoriteMemberInfo?.avatar?.uri,
          })
        )
        return router.push(
          generatePath(routerPages.ArtistMembershipPurchaseProceed, {
            artistId,
            productId: router.query.productId,
          })
        )
      },
      onSelectFavoriteMember(favoriteMemberId: string) {
        setSelectedFavoriteMember(favoriteMemberId)
      },
    },
  }
})

const QUERY = gql`
  query ArtistMembershipFavoriteMembersSelect($artistId: ID) {
    artist(id: $artistId) {
      displayName
      avatar {
        uri
      }
      ...ArtistMembershipFavoriteMembers
      ...ArtistWithPatron
    }
  }
  ${ArtistMembershipFavoriteMembersFragmentDoc}
  ${ArtistWithPatronFragmentDoc}
`
